import React from "react";

import { getUtcTime } from "./utils";

export const COLORS = [
  "#3d6fc7",
  "#ffc009",
  "#ff8d3e",
  "#f4635e",
  "#cc4975",
  "#92407e",
  "#805dac",
];

export const SINGLE_METRIC_CHARTS = ["pie", "funnel"];

export const INITIAL_VALUES = {
  title: "",
  schema: "",
  table: "",
  chart_type: "",
  namekey: "",
  valuekey: "",
  filters: "",
  sortBy: "",
  sortDirection: true,
  row_limit: { label: "10000", value: 10000 },
};

export const CHART_TYPES = [
  { label: "Tabular", value: "tabular" },
  { label: "Pie Chart", value: "pie" },
  { label: "Bar Chart", value: "bar" },
  { label: "Line Chart", value: "line" },
  { label: "Scatter Chart", value: "scatter" },
  { label: "Funnel Chart", value: "funnel" },
  { label: "Radar Chart", value: "radar" },
  { label: "Treemap Chart", value: "tree" },
  { label: "Area Chart", value: "area" },
];

export const AGGREGATE_FUNCTIONS = [
  { label: "AVG", value: "AVG(" },
  { label: "COUNT", value: "COUNT(" },
  { label: "COUNT_DISTINCT", value: "COUNT(DISTINCT " },
  { label: "MAX", value: "MAX(" },
  { label: "MIN", value: "MIN(" },
  { label: "SUM", value: "SUM(" },
];

export const ROW_LIMITS = [
  { label: "10", value: 10 },
  { label: "100", value: 100 },
  { label: "1000", value: 1000 },
  { label: "10000", value: 10000 },
  { label: "100000", value: 100000 },
  { label: "1000000", value: 1000000 },
  { label: "10000000", value: 10000000 },
  { label: "100000000", value: 100000000 },
];

export const PAGE_SIZE_OPTIONS = [10, 50, 100, 200, 500];

export const OPERATORS = [
  { label: "EQUALS", value: "=" },
  { label: "NOT EQUALS", value: "<>" },
  { label: "<", value: "<" },
  { label: ">", value: ">" },
  { label: "<=", value: "<=" },
  { label: ">=", value: ">=" },
  { label: "IN", value: "IN" },
  { label: "NOT IN", value: "NOT IN" },
  { label: "LIKE", value: "LIKE" },
  { label: "IS NULL", value: "IS NULL" },
  { label: "IS NOT NULL", value: "IS NOT NULL" },
];

export const QA_COLUMN = title => [
  { title: title, key: title, dataIndex: title },
];

export const DATA_QA_PAGE_SIZE_OPTIONS_LIST = [10, 25, 50, 75, 100];

export const SURVIVORSHIP_DOMAIN_BUSINESS_RULES_COLUMNS = [
  {
    key: "Business Rule No.",
    dataIndex: "business_rule_number",
    title: "Business Rule No.",
    width: 170,
    align: "center",
    ellipsis: true,
    sorter: true,
  },
  {
    key: "Critical Data Element",
    dataIndex: "critical_data_element",
    title: "Critical Data Element",
    width: 250,
    ellipsis: true,
    sorter: true,
  },
  {
    key: "Business Rule",
    dataIndex: "business_rule",
    title: "Business Rule",
    width: 450,
    sorter: true,
  },
  {
    key: "Business Rule Type",
    dataIndex: "business_rule_type",
    title: "Business Rule Type",
    width: 250,
    ellipsis: true,
    sorter: true,
  },
  {
    key: "Context",
    dataIndex: "context",
    title: "Context",
    width: 250,
    ellipsis: true,
    sorter: true,
  },
  {
    key: "Source Name",
    dataIndex: "source_name",
    title: "Source Name",
    width: 250,
    ellipsis: true,
    sorter: true,
  },
  {
    key: "Domain",
    dataIndex: "domain",
    title: "Domain",
    width: 250,
    ellipsis: true,
    sorter: true,
  },
  {
    key: "Business Rule Category",
    dataIndex: "business_rule_category",
    title: "Business Rule Category",
    width: 250,
    ellipsis: true,
    sorter: true,
  },
  {
    key: "Approver",
    dataIndex: "approver",
    title: "Approver",
    width: 250,
    ellipsis: true,
    sorter: true,
  },
  {
    key: "Approval Date",
    dataIndex: "approval_date",
    title: "Approval Date",
    width: 200,
    ellipsis: true,
    sorter: true,
  },
];

export const SURVIVORSHIP_DOMAIN_BUSINESS_RULES_INITIAL_VALUES = {
  rule_no: "",
  rule: "",
  context: "",
  ruleType: "",
  sourceName: "",
  ruleCategory: "",
  domain: "",
  cde: "",
  approver: "",
  approvalDate: "",
};

export const SURVIVORSHIP_BUSINESS_RULES_HISTORY_COLUMNS = [
  {
    dataIndex: "business_rule_number",
    key: "business_rule_number",
    title: "Rule No.",
    width: 100,
    align: "center",
    ellipsis: true,
    sorter: true,
  },
  {
    dataIndex: "event",
    key: "event",
    title: "Event",
    width: 100,
    align: "center",
    ellipsis: true,
    sorter: true,
  },
  {
    dataIndex: "user",
    key: "user_name",
    title: "Author",
    width: 150,
    ellipsis: true,
    render: val => `${val?.first_name} ${val?.last_name}`,
    sorter: true,
  },
  {
    dataIndex: "user",
    key: "user_email",
    title: "Author Email",
    width: 200,
    ellipsis: true,
    render: val => `${val?.email}`,
    sorter: true,
  },
  {
    dataIndex: "change_diff",
    key: "change_diff",
    title: "Changes",
    width: 300,
    render: val => {
      const changesAsString = Object.entries(val)
        .map(
          ([column, changes]) =>
            `${column}: ${changes[0] || "Nil"} => ${changes[1] || "Nil"}`
        )
        .join("\n");

      const changes = changesAsString.split("\n");
      const changesHTML = changes.map((change, index) => (
        <div key={index}>
          <b>{change.split(":")[0]}</b>
          <i>:{change.split(":")[1]}</i>
        </div>
      ));

      return changesHTML;
    },
  },
  {
    dataIndex: "created_at",
    key: "created_at",
    title: "Action Time",
    width: 240,
    align: "center",
    render: val => getUtcTime(val),
    sorter: true,
  },
];

export const SURVIVORSHIP_DOMAIN_SOURCE_NAMES = [
  "CVO Aperture",
  "Provider Manager",
  "AHCCCS",
  "NPPES",
  "Fortis",
  "MSOW",
  "Apogee",
];
